import { PRIORITY_LABEL, PRIORITY_ORDER, type Priority, type ViewConfig } from '@planning/contracts';
import type { TaskItem } from '@/api/types';

/**
 * Lo que una vista hace con las tareas que trae: filtrar, ordenar y agrupar según su configuración (spec §3.1). Todas
 * las vistas reciben las mismas tareas del API y aplican aquí su configuración.
 */
export function today(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
}

function fieldValue(t: TaskItem, field: string): unknown {
  switch (field) {
    case 'assignee': return t.assigneeId;
    case 'team': return t.teamId;
    case 'priority': return t.priority;
    case 'label': return t.labelIds;
    case 'section': return t.sectionId;
    case 'due': case 'dueDate': return t.dueDate;
    case 'start': case 'startDate': return t.startDate;
    case 'completed': return !!t.completedAt;
    case 'title': return t.title;
    default:
      if (field.startsWith('cf:')) return t.customFields[field.slice(3)];
      return (t as unknown as Record<string, unknown>)[field];
  }
}

export function applyFilters(tasks: TaskItem[], config: ViewConfig | undefined): TaskItem[] {
  if (!config) return tasks;
  let out = config.showCompleted === false ? tasks.filter((t) => !t.completedAt) : tasks;
  for (const f of config.filters ?? []) {
    out = out.filter((t) => {
      const v = fieldValue(t, f.field);
      const want = f.value;
      switch (f.operator) {
        case 'is': return Array.isArray(v) ? v.includes(want as string) : v === want || (want === 'me' && v === '__me__');
        case 'is_not': return Array.isArray(v) ? !v.includes(want as string) : v !== want;
        case 'in': return Array.isArray(want) && (Array.isArray(v) ? v.some((x) => want.includes(x as string)) : want.includes(v as string));
        case 'contains': return String(v ?? '').toLowerCase().includes(String(want ?? '').toLowerCase());
        case 'before': return !!v && String(v) < String(want === 'today' ? today() : want);
        case 'after': return !!v && String(v) > String(want === 'today' ? today() : want);
        case 'empty': return v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
        case 'not_empty': return !(v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0));
        default: return true;
      }
    });
  }
  return applySort(out, config.sort);
}

export function applySort(tasks: TaskItem[], sort: ViewConfig['sort'] | undefined): TaskItem[] {
  if (!sort?.length) return tasks;
  const prio = (p: Priority | null) => (p ? PRIORITY_ORDER.indexOf(p) : 9);
  return [...tasks].sort((a, b) => {
    for (const s of sort) {
      const dir = s.dir === 'desc' ? -1 : 1;
      let c = 0;
      if (s.field === 'priority') c = prio(a.priority) - prio(b.priority);
      else {
        const x = fieldValue(a, s.field);
        const y = fieldValue(b, s.field);
        if (x == null && y != null) c = 1;
        else if (y == null && x != null) c = -1;
        else c = String(x ?? '').localeCompare(String(y ?? ''), 'es', { numeric: true });
        if (c !== 0 && (x == null || y == null)) return c; // vacíos siempre al final
      }
      if (c !== 0) return c * dir;
    }
    return 0;
  });
}

export interface Group {
  id: string;
  title: string;
  color?: number;
  tasks: TaskItem[];
}

/** Grupos para Kanban y Tabla. Agrupar por un campo distinto de la sección cambia ese campo al arrastrar. */
export function groupTasks(tasks: TaskItem[], groupBy: string | null | undefined, ctx: { sections: Array<{ id: string; name: string }>; people: Map<string, { nombre: string; color?: number }>; labels: Array<{ id: string; name: string; color: number }>; teams?: { names: Map<string, string>; none: string } }): Group[] {
  const by = groupBy || 'section';
  if (by === 'section') {
    const groups = ctx.sections.map((s) => ({ id: s.id, title: s.name, tasks: tasks.filter((t) => t.sectionId === s.id) }));
    const orphan = tasks.filter((t) => !t.sectionId || !ctx.sections.some((s) => s.id === t.sectionId));
    return orphan.length ? [...groups, { id: '__none', title: 'Sin sección', tasks: orphan }] : groups;
  }
  if (by === 'priority') return [...PRIORITY_ORDER.map((p) => ({ id: p, title: PRIORITY_LABEL[p], color: p === 'URGENT' ? 2 : p === 'HIGH' ? 7 : p === 'MEDIUM' ? 4 : 10, tasks: tasks.filter((t) => t.priority === p) })), { id: '__none', title: 'Sin prioridad', tasks: tasks.filter((t) => !t.priority) }];
  if (by === 'assignee') {
    const ids = [...new Set(tasks.map((t) => t.assigneeId).filter(Boolean) as string[])];
    return [...ids.map((id) => ({ id, title: ctx.people.get(id)?.nombre ?? 'Persona', color: ctx.people.get(id)?.color, tasks: tasks.filter((t) => t.assigneeId === id) })), { id: '__none', title: 'Sin asignar', tasks: tasks.filter((t) => !t.assigneeId) }];
  }
  if (by === 'team') {
    const ids = [...new Set(tasks.map((t) => t.teamId).filter(Boolean) as string[])];
    return [...ids.map((id) => ({ id, title: ctx.teams?.names.get(id) ?? '—', tasks: tasks.filter((t) => t.teamId === id) })), { id: '__none', title: ctx.teams?.none ?? '—', tasks: tasks.filter((t) => !t.teamId) }];
  }
  if (by === 'label') return [...ctx.labels.map((l) => ({ id: l.id, title: l.name, color: l.color, tasks: tasks.filter((t) => t.labelIds.includes(l.id)) })), { id: '__none', title: 'Sin etiqueta', tasks: tasks.filter((t) => !t.labelIds.length) }];
  if (by === 'completed') return [{ id: 'open', title: 'Abiertas', tasks: tasks.filter((t) => !t.completedAt) }, { id: 'done', title: 'Completadas', color: 3, tasks: tasks.filter((t) => t.completedAt) }];
  if (by === 'due') {
    const t0 = today();
    const week = new Date(Date.parse(t0) + 7 * 86_400_000).toISOString().slice(0, 10);
    return [
      { id: 'overdue', title: 'Vencidas', color: 2, tasks: tasks.filter((t) => t.dueDate && t.dueDate < t0) },
      { id: 'today', title: 'Hoy', color: 4, tasks: tasks.filter((t) => t.dueDate === t0) },
      { id: 'week', title: 'Próximos 7 días', tasks: tasks.filter((t) => t.dueDate && t.dueDate > t0 && t.dueDate <= week) },
      { id: 'later', title: 'Más adelante', tasks: tasks.filter((t) => t.dueDate && t.dueDate > week) },
      { id: 'nodate', title: 'Sin fecha', tasks: tasks.filter((t) => !t.dueDate) },
    ];
  }
  if (by === 'project') {
    const ids = [...new Set(tasks.map((t) => t.projectId))];
    return ids.map((id) => ({ id, title: '', tasks: tasks.filter((t) => t.projectId === id) }));
  }
  if (by.startsWith('cf:')) return [{ id: 'all', title: 'Todas', tasks }];
  return [{ id: 'all', title: 'Todas', tasks }];
}

/** Qué cambia al soltar una tarea en un grupo cuando no se agrupa por sección. */
export function patchForGroup(groupBy: string, groupId: string): Record<string, unknown> | null {
  const value = groupId === '__none' ? null : groupId;
  if (groupBy === 'priority') return { priority: value };
  if (groupBy === 'assignee') return { assigneeId: value };
  if (groupBy === 'team') return { teamId: value };
  if (groupBy === 'completed') return { completed: groupId === 'done' };
  return null;
}

export const isOverdue = (t: Pick<TaskItem, 'dueDate' | 'completedAt'>) => !!t.dueDate && !t.completedAt && t.dueDate < today();

export function formatDate(iso: string | null | undefined, withYear = false): string {
  if (!iso) return '';
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', ...(withYear || d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}) });
}
